/**
 * Invoice list (design §B): tabs with server counts, search and filter
 * selects that map to GET /invoices (listFilters.ts), the receivables
 * summary strip, a selectable grid with a row menu, bulk bar, and
 * "Load more" paging over the opaque cursor.
 *
 * Phase 2 scope: "Send or share", "Send reminder" and delivery tags arrive
 * with messaging (Phases 3–5); the delivery column is kept and shows "—".
 * "Created by" is not offered: the list returns a display name while the
 * server filters on the user id, so a typed name would never match.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InvoiceRepository, {
  InvoiceListItem, ListQuery, ListResult, ListSummary,
} from '../../../internals/repository/InvoiceRepository';
import { InvoiceApiError } from '../../../internals/repository/authedPosApi';
import ShareDialog from '../components/ShareDialog';
import StatusChip from '../components/StatusChip';
import { date, dueHint, money, todayColombo } from '../format';
import {
  AMOUNT_OPTIONS, DUE_OPTIONS, EMPTY_FILTERS, ISSUED_OPTIONS, ListFilters, TABS, TabKey,
  isFiltered, selectedBalance, toCsv, toListQuery,
} from '../listFilters';
import { useSelectedShop } from '../useInvoicing';

const COLS = '20px 150px minmax(190px,1.4fr) 96px 130px 120px 120px 168px minmax(170px,1fr) 34px';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const OPEN_STATUSES = ['UNPAID', 'PARTLY_PAID', 'OVERDUE', 'PENDING_APPROVAL'];

function errorText(e: unknown): string {
  if (e instanceof InvoiceApiError) {
    if (e.code === 'forbidden_role' || e.status === 403) return 'Ask the shop owner or an admin to do this.';
    return e.message;
  }
  return 'Something went wrong. Try again.';
}

function downloadText(name: string, text: string) {
  // BOM so Excel reads the file as UTF-8.
  const blob = new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type LoadState = 'loading' | 'error' | 'ready';

export default function InvoiceListView() {
  const navigate = useNavigate();
  const shop = useSelectedShop();
  const shopId: string | undefined = shop?.shopId;
  const currency: string = shop?.currency || 'LKR';

  const [filters, setFilters] = useState<ListFilters>(EMPTY_FILTERS);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<InvoiceListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [summary, setSummary] = useState<ListSummary | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [picked, setPicked] = useState<Record<string, InvoiceListItem>>({});
  const [menu, setMenu] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [shareFor, setShareFor] = useState<InvoiceListItem | null>(null);
  const reqId = useRef(0);

  const today = todayColombo();
  const query: ListQuery = useMemo(() => toListQuery(filters, today), [filters, today]);
  const queryKey = JSON.stringify(query);

  // Debounce the search box into the query.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.q === search ? f : { ...f, q: search })), 300);
    return () => clearTimeout(t);
  }, [search]);

  const flash = useCallback((text: string) => setToast(text), []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const apply = (r: ListResult, append: boolean) => {
    setItems((prev) => (append ? [...prev, ...r.items] : r.items));
    setNextCursor(r.nextCursor);
    setCounts(r.counts || {});
    setSummary(r.summary || null);
  };

  const load = useCallback(() => {
    if (!shopId) return;
    const id = ++reqId.current;
    setState('loading');
    setPicked({});
    setMenu(null);
    InvoiceRepository.list(shopId, JSON.parse(queryKey))
      .then((r) => { if (id === reqId.current) { apply(r, false); setState('ready'); } })
      .catch(() => { if (id === reqId.current) setState('error'); });
  }, [shopId, queryKey]);

  // Re-runs on a new shop or a new query.
  useEffect(() => { load(); }, [load]);

  const loadMore = () => {
    if (!shopId || !nextCursor || loadingMore) return;
    const id = reqId.current;
    setLoadingMore(true);
    InvoiceRepository.list(shopId, { ...query, cursor: nextCursor })
      .then((r) => { if (id === reqId.current) apply(r, true); })
      .catch((e) => flash(errorText(e)))
      .finally(() => setLoadingMore(false));
  };

  // Escape or a click outside closes the row menu.
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest?.('[data-row-menu]')) setMenu(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [menu]);

  const setTab = (tab: TabKey) => setFilters((f) => ({ ...f, tab }));
  const setF = <K extends keyof ListFilters>(k: K, v: ListFilters[K]) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => { setSearch(''); setFilters((f) => ({ ...EMPTY_FILTERS, tab: f.tab })); };
  const filtered = isFiltered({ ...filters, q: search });

  const pickedList = Object.values(picked);
  const allOn = items.length > 0 && items.every((it) => picked[it.docId]);
  const toggleAll = () => {
    if (allOn) setPicked({});
    else setPicked(Object.fromEntries(items.map((it) => [it.docId, it])));
  };
  const togglePick = (it: InvoiceListItem) => setPicked((p) => {
    const next = { ...p };
    if (next[it.docId]) delete next[it.docId];
    else next[it.docId] = it;
    return next;
  });

  const exportCsv = async (rows?: InvoiceListItem[]) => {
    if (!shopId) return;
    try {
      setExporting(true);
      const list = rows ?? await InvoiceRepository.listAll(shopId, { ...query, cursor: undefined });
      downloadText(`invoices-${today}.csv`, toCsv(list, currency));
      flash(`invoices-${today}.csv downloaded`);
    } catch (e) {
      flash(errorText(e));
    } finally {
      setExporting(false);
    }
  };

  const downloadPdf = async (it: InvoiceListItem) => {
    if (!shopId) return;
    setMenu(null);
    // Open the tab inside the click so the pop-up blocker allows it.
    const w = window.open('', '_blank');
    try {
      const { url } = await InvoiceRepository.pdfUrl(shopId, it.docId);
      if (w) { w.opener = null; w.location.href = url; } else if (!window.open(url, '_blank', 'noopener')) {
        flash('Allow pop-ups for this site to download PDFs.');
      }
    } catch (e) {
      w?.close();
      flash(errorText(e));
    }
  };

  const bulkPdf = async () => {
    if (!shopId) return;
    const issued = pickedList.filter((it) => it.displayStatus !== 'DRAFT');
    const drafts = pickedList.length - issued.length;
    let blocked = 0;
    let failed = 0;
    for (const it of issued) {
      try {
        const { url } = await InvoiceRepository.pdfUrl(shopId, it.docId);
        if (!window.open(url, '_blank', 'noopener')) blocked += 1;
      } catch (e) {
        failed += 1;
      }
    }
    const notes = [`${issued.length - blocked - failed} PDFs opened`];
    if (drafts) notes.push(`${drafts} draft${drafts === 1 ? '' : 's'} skipped`);
    if (blocked) notes.push(`${blocked} blocked, allow pop-ups for this site`);
    if (failed) notes.push(`${failed} could not be prepared`);
    flash(notes.join(' · '));
  };

  const duplicate = async (it: InvoiceListItem) => {
    if (!shopId) return;
    setMenu(null);
    try {
      const { document } = await InvoiceRepository.duplicate(shopId, it.docId);
      navigate(`/invoices/${encodeURIComponent(document.docId)}/edit`);
    } catch (e) {
      flash(errorText(e));
    }
  };

  const open = (it: InvoiceListItem, suffix = '') => navigate(`/invoices/${encodeURIComponent(it.docId)}${suffix}`);

  const menuFor = (it: InvoiceListItem) => {
    const s = it.displayStatus;
    const m: { label: string; run: () => void; danger?: boolean }[] = [{ label: 'View', run: () => open(it) }];
    if (s === 'DRAFT') m.push({ label: 'Edit draft', run: () => open(it, '/edit') });
    if (OPEN_STATUSES.includes(s)) m.push({ label: 'Record payment', run: () => open(it, '?action=pay') });
    if (s !== 'DRAFT') m.push({ label: 'Send or share', run: () => { setMenu(null); setShareFor(it); } });
    if (s !== 'DRAFT') m.push({ label: 'Download PDF', run: () => downloadPdf(it) });
    m.push({ label: 'Duplicate', run: () => duplicate(it) });
    if (s !== 'DRAFT' && s !== 'VOID' && s !== 'PAID') m.push({ label: 'Void', run: () => open(it, '?action=void'), danger: true });
    return m;
  };

  const noInvoicesYet = !filtered && filters.tab === 'ALL';
  const month = MONTHS[Number(today.slice(5, 7)) - 1];

  return (
    <div className="h-screen flex flex-col w-full p-5 min-[1000px]:px-8 min-[1000px]:py-7 overflow-auto [&>*]:shrink-0 gap-[18px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="m-0 text-[28px] font-semibold tracking-[-0.015em] text-text1">Invoices</h1>
          <span className="text-sm text-text2">Create, send and follow up. Drafts are not visible to customers.</span>
        </div>
        <button type="button" className="nl-btn nl-btn--primary" onClick={() => navigate('/invoices/new')}>
          + New invoice
        </button>
      </div>

      <SummaryStrip
        summary={summary}
        loading={state === 'loading' && !summary}
        openCount={counts.UNPAID}
        month={month}
        currency={currency}
        onTab={setTab}
      />

      <div role="tablist" aria-label="Invoice status" className="flex gap-1 overflow-x-auto border-b border-borderc">
        {TABS.map((t) => {
          const on = filters.tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setTab(t.key)}
              className={`-mb-px flex h-10 items-center gap-[7px] whitespace-nowrap border-b-2 bg-transparent px-3 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${
                on ? 'border-brand text-brand' : 'border-transparent text-text2 hover:text-text1'
              }`}
            >
              {t.label}
              <span
                className={`grid h-[18px] min-w-[20px] place-items-center rounded-[9px] px-1.5 text-[11px] font-bold ${on ? 'bg-brand-soft text-brand' : 'text-text2'}`}
                style={on ? undefined : { background: 'var(--neutralSoft)' }}
              >
                {counts[t.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search number, customer or phone"
          aria-label="Search invoices"
          className="nl-input nl-input--sm !h-9 max-w-[340px] flex-[1_1_260px] text-[13px]"
        />
        <FilterSelect label="Issued" value={filters.issued} set={filters.issued !== 'ALL'} options={ISSUED_OPTIONS} onChange={(v) => setF('issued', v)} />
        <FilterSelect label="Due" value={filters.due} set={filters.due !== 'ANY'} options={DUE_OPTIONS} onChange={(v) => setF('due', v)} />
        <FilterSelect label="Amount" value={filters.amount} set={filters.amount !== 'ANY'} options={AMOUNT_OPTIONS} onChange={(v) => setF('amount', v)} />
        {filtered && (
          <button type="button" onClick={clearFilters} className="bg-transparent px-1 text-[13px] font-semibold text-brand hover:underline">
            Clear filters
          </button>
        )}
        <span className="flex-1" />
        <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm !h-9" disabled={exporting || !shopId} onClick={() => exportCsv()}>
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {pickedList.length > 0 && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-brand bg-brand-soft px-3.5 py-2.5">
          <span className="font-semibold text-text1">{pickedList.length} selected</span>
          <span className="text-text2 tabular-nums">· {money(selectedBalance(pickedList), currency)} balance</span>
          <span className="flex-1" />
          <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm" onClick={bulkPdf}>Download PDFs</button>
          <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm" onClick={() => exportCsv(pickedList)}>Export CSV</button>
          <button type="button" className="nl-btn nl-btn--ghost nl-btn--sm" onClick={() => setPicked({})}>Clear</button>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-borderc bg-surface">
        <div className="min-w-[1220px]" role="table" aria-label="Invoices">
          <div
            role="row"
            className="grid items-center gap-3 bg-surface2 px-[18px] py-2.5 text-[11px] font-semibold uppercase tracking-[.07em] text-text3"
            style={{ gridTemplateColumns: COLS }}
          >
            <input type="checkbox" className="nl-check" checked={allOn} onChange={toggleAll} aria-label="Select all" disabled={!items.length} />
            <span role="columnheader">Number</span>
            <span role="columnheader">Customer</span>
            <span role="columnheader">Issued</span>
            <span role="columnheader">Due</span>
            <span role="columnheader" className="text-right">Total</span>
            <span role="columnheader" className="text-right">Balance</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Delivery</span>
            <span />
          </div>

          {state === 'loading' && [1, 2, 3, 4, 5].map((k) => (
            <div key={k} className="grid items-center gap-3 border-t border-borderc px-[18px] py-4" style={{ gridTemplateColumns: COLS }} aria-hidden>
              <span />
              {[100, 70, 100, 100, 100, 100].map((w, i) => <Bar key={i} w={`${w}%`} />)}
              <Bar w="70px" h={18} />
              <span />
              <span />
            </div>
          ))}

          {state === 'error' && (
            <div className="flex flex-col items-center gap-2.5 border-t border-borderc px-5 py-12 text-center">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-neg-soft font-bold" style={{ color: 'var(--negFg)' }}>!</span>
              <span className="text-base font-semibold text-text1">Invoices could not load</span>
              <span className="max-w-[420px] text-text2">Check the connection and try again. Nothing has been lost.</span>
              <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm !h-9" onClick={load}>Try again</button>
            </div>
          )}

          {state === 'ready' && items.length === 0 && (
            <div className="flex flex-col items-center gap-2.5 border-t border-borderc px-5 py-14 text-center">
              <span className="text-[17px] font-semibold text-text1">{noInvoicesYet ? 'No invoices yet' : 'No invoices match'}</span>
              <span className="max-w-[440px] text-text2">
                {noInvoicesYet
                  ? 'Create an invoice for a customer and send it by WhatsApp, SMS or email. They can view and pay from the link.'
                  : 'Try another tab, or clear the search and filters.'}
              </span>
              {noInvoicesYet ? (
                <button type="button" className="nl-btn nl-btn--primary" onClick={() => navigate('/invoices/new')}>+ New invoice</button>
              ) : filtered && (
                <button type="button" className="nl-btn nl-btn--secondary nl-btn--sm !h-9" onClick={clearFilters}>Clear filters</button>
              )}
            </div>
          )}

          {state === 'ready' && items.map((it) => (
            <Row
              key={it.docId}
              it={it}
              currency={currency}
              today={today}
              picked={!!picked[it.docId]}
              onPick={() => togglePick(it)}
              onOpen={() => open(it)}
              menuOpen={menu === it.docId}
              onMenu={() => setMenu(menu === it.docId ? null : it.docId)}
              menu={menuFor(it)}
            />
          ))}
        </div>
      </div>

      {state === 'ready' && nextCursor && (
        <div className="flex justify-center pb-6">
          <button type="button" className="nl-btn nl-btn--secondary" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {shareFor && shopId && (
        <ShareDialog
          open
          onClose={() => setShareFor(null)}
          shopId={shopId}
          docId={shareFor.docId}
          document={shareFor}
          onShared={() => {}}
        />
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-[10px] bg-text1 px-4 py-2.5 text-sm font-medium text-surface"
          style={{ boxShadow: 'var(--shadowLg)' }}
        >
          <span className="text-pos" aria-hidden>✓</span>
          {toast}
        </div>
      )}
    </div>
  );
}

function Bar({ w, h = 10 }: { w: string; h?: number }) {
  return <span className="block animate-pulse" style={{ width: w, height: h, borderRadius: h / 2, background: 'var(--neutralSoft)' }} />;
}

function FilterSelect<V extends string>({ label, value, set, options, onChange }: {
  label: string; value: V; set: boolean; options: { value: V; label: string }[]; onChange: (v: V) => void;
}) {
  return (
    <label
      className="flex h-9 items-center gap-1.5 rounded-lg border bg-surface pl-2.5 pr-1 text-[13px] text-text2 focus-within:ring-2 focus-within:ring-brand-soft"
      style={{ borderColor: set ? 'var(--brand)' : 'var(--border2)' }}
    >
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as V)}
        className="h-8 cursor-pointer border-none bg-transparent pr-1 text-[13px] font-semibold text-text1 outline-none"
      >
        {options.map((o) => <option key={o.value} value={o.value} className="bg-surface text-text1">{o.label}</option>)}
      </select>
    </label>
  );
}

function SummaryStrip({ summary, loading, openCount, month, currency, onTab }: {
  summary: ListSummary | null; loading: boolean; openCount?: number; month: string; currency: string; onTab: (t: TabKey) => void;
}) {
  const cards: {
    label: string; value: string; sub: string; danger?: boolean; pill?: string; onClick?: () => void;
  }[] = [
    {
      label: 'Outstanding',
      value: money(summary?.outstandingMinor ?? 0, currency),
      sub: `${openCount ?? 0} open invoice${openCount === 1 ? '' : 's'}`,
      onClick: () => onTab('UNPAID'),
    },
    {
      label: 'Overdue',
      value: money(summary?.overdueMinor ?? 0, currency),
      sub: `${summary?.overdueCount ?? 0} invoice${summary?.overdueCount === 1 ? '' : 's'} past due`,
      danger: true,
      pill: String(summary?.overdueCount ?? 0),
      onClick: () => onTab('OVERDUE'),
    },
    {
      label: 'Collected this month',
      value: money(summary?.collectedThisMonthMinor ?? 0, currency),
      sub: `Payments in ${month}`,
    },
    {
      label: 'Average days to pay',
      value: summary?.averageDaysToPay == null ? '—' : `${summary.averageDaysToPay} days`,
      sub: 'From issue to paid',
    },
  ];
  return (
    <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
      {cards.map((c) => {
        const body = (
          <>
            <span className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[.08em] text-text3">{c.label}</span>
              {c.pill && !loading && (
                <span className="rounded-full bg-neg-soft px-2 py-0.5 text-[11px] font-bold" style={{ color: 'var(--negFg)' }}>{c.pill}</span>
              )}
            </span>
            {loading ? <Bar w="60%" h={22} /> : (
              <span
                data-testid={`summary-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="nl-num whitespace-nowrap text-[22px] font-semibold tabular-nums"
                style={{ color: c.danger ? 'var(--negFg)' : 'var(--text)' }}
              >
                {c.value}
              </span>
            )}
            <span className="text-xs text-text2">{loading ? ' ' : c.sub}</span>
          </>
        );
        const cls = 'flex flex-col gap-2.5 rounded-2xl border border-borderc bg-surface px-5 py-[18px] text-left text-text1';
        return c.onClick ? (
          <button key={c.label} type="button" onClick={c.onClick} className={`${cls} hover:border-borderc2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand`}>
            {body}
          </button>
        ) : (
          <div key={c.label} className={cls}>{body}</div>
        );
      })}
    </div>
  );
}

function Row({ it, currency, today, picked, onPick, onOpen, menuOpen, onMenu, menu }: {
  it: InvoiceListItem; currency: string; today: string; picked: boolean; onPick: () => void; onOpen: () => void;
  menuOpen: boolean; onMenu: () => void; menu: { label: string; run: () => void; danger?: boolean }[];
}) {
  const s = it.displayStatus;
  const overdue = s === 'OVERDUE';
  const hint = OPEN_STATUSES.includes(s) ? dueHint(it.dueDate, today) : null;
  const c = it.customer || {};
  const main = c.companyName || c.name || '—';
  const sub = c.companyName ? c.name : c.phoneE164;
  const label = it.number || 'Draft';
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div
      role="row"
      data-testid="invoice-row"
      onClick={onOpen}
      className={`grid cursor-pointer items-center gap-3 border-t border-borderc px-[18px] py-3 ${picked ? 'bg-brand-soft' : 'hover:bg-surface2'}`}
      style={{ gridTemplateColumns: COLS }}
    >
      <input type="checkbox" className="nl-check" checked={picked} onChange={onPick} onClick={stop} aria-label={`Select ${label}`} />
      <button type="button" onClick={(e) => { stop(e); onOpen(); }} className="nl-num truncate bg-transparent p-0 text-left text-[13px] font-semibold text-text1">
        {label}
      </button>
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-semibold text-text1">{main}</span>
        {sub && <span className={`truncate text-xs text-text3 ${c.companyName ? '' : 'nl-num'}`}>{sub}</span>}
      </span>
      <span className="text-[13px] text-text2">{date(it.issueDate)}</span>
      <span className="flex flex-col">
        <span className={`text-[13px] ${overdue ? 'font-semibold' : 'text-text2'}`} style={overdue ? { color: 'var(--negFg)' } : undefined}>
          {it.dueDate ? date(it.dueDate) : '—'}
        </span>
        {hint && (
          <span className={`text-xs font-semibold ${hint.overdue ? '' : 'text-text3'}`} style={hint.overdue ? { color: 'var(--negFg)' } : undefined}>
            {hint.text}
          </span>
        )}
      </span>
      <span className="text-right tabular-nums text-text2">{money(it.totalMinor, currency)}</span>
      <span className="text-right font-semibold tabular-nums text-text1">{s === 'VOID' ? '—' : money(Math.max(0, it.balanceMinor), currency)}</span>
      <span><StatusChip status={s} /></span>
      <span className="text-xs text-text3">—</span>
      <div className="relative flex justify-end" data-row-menu onClick={stop}>
        <button
          type="button"
          onClick={onMenu}
          aria-label={`Actions for ${label}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="h-[30px] w-8 rounded-[7px] border border-borderc2 bg-surface text-[15px] font-bold leading-none text-text2 hover:bg-surface2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
        >
          ⋯
        </button>
        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-9 z-30 flex min-w-[200px] flex-col rounded-xl border border-borderc2 bg-surface p-1.5"
            style={{ boxShadow: 'var(--shadowLg)' }}
          >
            {menu.map((m, i) => (
              <button
                key={m.label}
                type="button"
                role="menuitem"
                autoFocus={i === 0}
                onClick={m.run}
                className={`rounded-[7px] bg-transparent px-2.5 py-2 text-left text-[13px] font-medium hover:bg-surface2 focus-visible:bg-surface2 focus-visible:outline-none ${m.danger ? '' : 'text-text1'}`}
                style={m.danger ? { color: 'var(--negFg)' } : undefined}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
